import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {CheckGlyph} from '@/components/ui/icons'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {SectionCard} from '@/components/section-card'
import {HelpIcon} from '@/components/help-icon'
import {
  EstadoDato,
  Procedencia,
  sinConfirmar,
  TrazabilidadSwitch,
  useTrazabilidad,
} from '@/components/perfil/provenance-note'
import {
  useAddHazard,
  useConfirmHazard,
  useDenyHazard,
  useHazards,
  useProfileFacts,
  useUpdateProfileFact,
  type CatalogHazard,
  type HazardTag,
} from '@/hooks/salud/perfil_riesgo/use-perfil'
import {translateApiError} from '@/lib/errors'

function SubstancesMultiSelect({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: facts} = useProfileFacts(businessId)
  const fact = facts?.find((f) => f.fact_key === 'sustancias_especificas')
  const update = useUpdateProfileFact(businessId)
  const selected = Array.isArray(fact?.value) ? (fact.value as string[]) : []
  const options = fact?.enum_values ?? []

  function toggle(option: string) {
    const next = selected.includes(option) ? selected.filter((o) => o !== option) : [...selected, option]
    update.mutate({fact_key: 'sustancias_especificas', value: next})
  }

  return (
    <div className="mt-2 space-y-1.5 border-t border-border pt-2.5" data-testid="substances-multiselect">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('perfilRiesgo.hazards.substancesLabel')}
      </p>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-1.5 text-sm text-foreground">
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => toggle(option)}
              data-testid={`substance-${option}`}
              className="accent-primary"
            />
            {t(`perfilRiesgo.hazards.substances.${option}`)}
          </label>
        ))}
      </div>
    </div>
  )
}

function PendingHazardRow({hazard, businessId, canEdit}: {hazard: HazardTag; businessId: string; canEdit: boolean}) {
  const {t} = useTranslation()
  const confirm = useConfirmHazard(businessId)
  const deny = useDenyHazard(businessId)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="surface-divider space-y-2 rounded-lg border p-4" data-testid={`hazard-pending-${hazard.tag_code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-foreground">{hazard.label}</p>
        {hazard.is_reask ? (
          <Tag tone="amber" data-testid={`hazard-estado-${hazard.tag_code}`}>
            {t('perfilRiesgo.hazards.reaskBadge')}
          </Tag>
        ) : (
          <EstadoDato estado="sinResponder" testId={`hazard-estado-${hazard.tag_code}`} />
        )}
      </div>
      <p className="text-xs text-muted-foreground">{hazard.description}</p>
      {canEdit && (
        <div className="flex gap-2">
          <Button
            size="sm"
            data-testid="confirm-hazard"
            disabled={confirm.isPending || deny.isPending}
            onClick={() => {
              setError(null)
              confirm.mutate(hazard.tag_code, {onError: (err) => setError(translateApiError(err))})
            }}>
            {t('perfilRiesgo.hazards.yesApplies')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="deny-hazard"
            disabled={confirm.isPending || deny.isPending}
            onClick={() => {
              setError(null)
              deny.mutate({tagCode: hazard.tag_code}, {onError: (err) => setError(translateApiError(err))})
            }}>
            {t('perfilRiesgo.hazards.noApplies')}
          </Button>
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

function ConfirmedHazardRow({
  hazard,
  businessId,
  desplegado,
}: {
  hazard: HazardTag
  businessId: string
  desplegado: boolean
}) {
  return (
    <div
      className="surface-divider space-y-1.5 rounded-lg border p-4"
      data-testid={`hazard-confirmed-${hazard.tag_code}`}>
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
          <CheckGlyph size={11} />
        </span>
        <p className="text-sm font-medium text-foreground">{hazard.label}</p>
        {sinConfirmar(hazard.confirmed_by, hazard.confirmed_at) && (
          <EstadoDato estado="pendiente" testId={`hazard-estado-${hazard.tag_code}`} />
        )}
      </div>
      <Procedencia confirmedBy={hazard.confirmed_by} confirmedAt={hazard.confirmed_at} desplegado={desplegado} />
      {hazard.tag_code === 'quimicos' && <SubstancesMultiSelect businessId={businessId} />}
    </div>
  )
}

function DeniedHazardRow({
  hazard,
  businessId,
  canEdit,
  desplegado,
}: {
  hazard: HazardTag
  businessId: string
  canEdit: boolean
  desplegado: boolean
}) {
  const {t} = useTranslation()
  const confirm = useConfirmHazard(businessId)
  return (
    <div className="surface-divider space-y-1.5 rounded-lg border p-4" data-testid={`hazard-denied-${hazard.tag_code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-foreground">{hazard.label}</p>
        {sinConfirmar(hazard.confirmed_by, hazard.denied_at) && (
          <EstadoDato estado="pendiente" testId={`hazard-estado-${hazard.tag_code}`} />
        )}
      </div>
      <Procedencia confirmedBy={hazard.confirmed_by} confirmedAt={hazard.denied_at} desplegado={desplegado} />
      {canEdit && (
        <Button
          size="sm"
          variant="outline"
          data-testid="reconsider-hazard"
          onClick={() => confirm.mutate(hazard.tag_code)}>
          {t('perfilRiesgo.hazards.yesApplies')}
        </Button>
      )}
    </div>
  )
}

function AddHazardPicker({
  businessId,
  addable,
  canEdit,
}: {
  businessId: string
  addable: CatalogHazard[]
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const add = useAddHazard(businessId)
  if (!canEdit) return null
  return (
    <div>
      <Button size="sm" variant="outline" data-testid="open-add-hazard" onClick={() => setOpen(!open)}>
        {t('perfilRiesgo.hazards.addAnother')}
      </Button>
      {open && (
        <ul className="mt-2 space-y-1.5">
          {addable.map((hazard) => (
            <li key={hazard.tag_code}>
              <button
                type="button"
                data-testid={`add-hazard-${hazard.tag_code}`}
                onClick={() => {
                  add.mutate(hazard.tag_code)
                  setOpen(false)
                }}
                className="w-full rounded-lg border border-input px-3 py-2 text-left text-sm hover:bg-secondary">
                <span className="font-medium text-foreground">{hazard.label}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{hazard.description}</span>
              </button>
            </li>
          ))}
          {addable.length === 0 && (
            <p className="text-xs text-muted-foreground">{t('perfilRiesgo.hazards.addNoneLeft')}</p>
          )}
        </ul>
      )}
    </div>
  )
}

export function HazardsSection({businessId, canEdit}: {businessId: string; canEdit: boolean}) {
  const {t} = useTranslation()
  const {data} = useHazards(businessId)
  const [desplegado, setDesplegado] = useTrazabilidad()
  if (!data) return null

  // La ayuda va en `actions` y no en `title`: el título es un `<h2>` y un `<button>` adentro
  // anida un interactivo y le cambia el nombre accesible.
  return (
    <SectionCard
      testId="perfil-hazards-section"
      title={t('perfilRiesgo.hazards.title')}
      actions={
        <div className="flex items-center gap-3">
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('perfilRiesgo.hazards.title')})}
            testId="hazards-tip">
            {t('perfilRiesgo.hazards.tip')}
          </HelpIcon>
          <TrazabilidadSwitch desplegado={desplegado} onChange={setDesplegado} testId="hazards-trazabilidad" />
        </div>
      }
      bodyClassName="space-y-4 p-5">
      {data.pending.length === 0 && data.confirmed.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="hazards-empty">
          {t('perfilRiesgo.hazards.emptySuggested')}
        </p>
      ) : (
        // Rótulo mono y no `GroupHeader`: este arrastra un `HelpIcon` y la sección ya tiene el suyo.
        <div className="space-y-4">
          {data.pending.length > 0 && (
            <div className="space-y-3" data-testid="hazards-grupo-pendientes">
              <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('perfilRiesgo.hazards.groupPending', {count: data.pending.length})}
              </p>
              {data.pending.map((hazard) => (
                <PendingHazardRow key={hazard.tag_code} hazard={hazard} businessId={businessId} canEdit={canEdit} />
              ))}
            </div>
          )}
          {data.confirmed.length > 0 && (
            <div className="space-y-3" data-testid="hazards-grupo-confirmados">
              <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('perfilRiesgo.hazards.groupConfirmed', {count: data.confirmed.length})}
              </p>
              {data.confirmed.map((hazard) => (
                <ConfirmedHazardRow
                  key={hazard.tag_code}
                  hazard={hazard}
                  businessId={businessId}
                  desplegado={desplegado}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <AddHazardPicker businessId={businessId} addable={data.addable} canEdit={canEdit} />

      {data.denied.length > 0 && (
        // La traza abre también los descartados: un `<details>` cerrado no se imprime (U10).
        // `|| undefined` lo deja no controlado cuando la traza está apagada.
        <details className="mt-2" open={desplegado || undefined}>
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            {t('perfilRiesgo.hazards.discardedTitle', {count: data.denied.length})}
          </summary>
          <div className="mt-2 space-y-2">
            {data.denied.map((hazard) => (
              <DeniedHazardRow
                key={hazard.tag_code}
                hazard={hazard}
                businessId={businessId}
                canEdit={canEdit}
                desplegado={desplegado}
              />
            ))}
          </div>
        </details>
      )}
    </SectionCard>
  )
}
